import { COLORS } from './colors';
import { FONTS } from './fonts';

export const STYLE_PRESET_IDS = [
  'meta_explainer_dark',
  'meta_explainer_cream',
  'meta_bold',
  'x_thread_dark',
  'x_thread_light',
  'x_bold',
] as const;
export type StylePresetId = (typeof STYLE_PRESET_IDS)[number];

export type StylePreset = {
  id: StylePresetId;
  label: string;
  background: {
    type: 'solid' | 'gradient';
    value: string;
  };
  text: {
    color: string;
    muted: string;
    accent: string;
    fontFamilyHeading: string;
    fontFamilyBody: string;
    fontFamilyMono: string;
  };
  card: {
    backgroundColor: string;
    borderRadius: number;
  };
  music: {
    baseVolume: number;
    duckedMultiplier: number;
    fadeSec: number;
  };
};

export const STYLE_PRESETS: Record<StylePresetId, StylePreset> = {
  meta_explainer_dark: {
    id: 'meta_explainer_dark',
    label: 'Meta Explainer (Dark)',
    background: {
      type: 'gradient',
      value: 'linear-gradient(135deg, #0b1020 0%, #1b1b4d 55%, #0b1020 100%)',
    },
    text: {
      color: COLORS.text.inverse,
      muted: 'rgba(255,255,255,0.75)',
      accent: '#38bdf8',
      fontFamilyHeading: FONTS.heading,
      fontFamilyBody: FONTS.body,
      fontFamilyMono: FONTS.mono,
    },
    card: {
      backgroundColor: 'rgba(0,0,0,0.35)',
      borderRadius: 18,
    },
    music: {
      baseVolume: 0.22,
      duckedMultiplier: 0.25,
      fadeSec: 0.22,
    },
  },

  meta_explainer_cream: {
    id: 'meta_explainer_cream',
    label: 'Meta Explainer (Cream)',
    background: {
      type: 'solid',
      value: COLORS.background.cream,
    },
    text: {
      color: COLORS.text.primary,
      muted: COLORS.text.secondary,
      accent: COLORS.accent.info,
      fontFamilyHeading: FONTS.heading,
      fontFamilyBody: FONTS.body,
      fontFamilyMono: FONTS.mono,
    },
    card: {
      backgroundColor: 'rgba(255,255,255,0.75)',
      borderRadius: 18,
    },
    music: {
      baseVolume: 0.18,
      duckedMultiplier: 0.3,
      fadeSec: 0.22,
    },
  },

  meta_bold: {
    id: 'meta_bold',
    label: 'Meta Bold',
    background: {
      type: 'gradient',
      value: 'radial-gradient(1200px circle at 20% 10%, #1f2937 0%, #000000 50%, #000000 100%)',
    },
    text: {
      color: COLORS.text.inverse,
      muted: 'rgba(255,255,255,0.75)',
      accent: '#a3e635',
      fontFamilyHeading: FONTS.heading,
      fontFamilyBody: FONTS.body,
      fontFamilyMono: FONTS.mono,
    },
    card: {
      backgroundColor: 'rgba(0,0,0,0.4)',
      borderRadius: 20,
    },
    music: {
      baseVolume: 0.24,
      duckedMultiplier: 0.22,
      fadeSec: 0.18,
    },
  },

  x_thread_dark: {
    id: 'x_thread_dark',
    label: 'X Thread (Dark)',
    background: {
      type: 'gradient',
      value: 'linear-gradient(180deg, #0a0a0a 0%, #15202b 50%, #0a0a0a 100%)',
    },
    text: {
      color: COLORS.text.inverse,
      muted: 'rgba(255,255,255,0.7)',
      accent: '#1d9bf0',
      fontFamilyHeading: FONTS.heading,
      fontFamilyBody: FONTS.body,
      fontFamilyMono: FONTS.mono,
    },
    card: {
      backgroundColor: 'rgba(29,155,240,0.08)',
      borderRadius: 16,
    },
    music: {
      baseVolume: 0.15,
      duckedMultiplier: 0.2,
      fadeSec: 0.3,
    },
  },

  x_thread_light: {
    id: 'x_thread_light',
    label: 'X Thread (Light)',
    background: {
      type: 'solid',
      value: '#f7f9f9',
    },
    text: {
      color: COLORS.text.primary,
      muted: COLORS.text.secondary,
      accent: '#1d9bf0',
      fontFamilyHeading: FONTS.heading,
      fontFamilyBody: FONTS.body,
      fontFamilyMono: FONTS.mono,
    },
    card: {
      backgroundColor: 'rgba(255,255,255,0.9)',
      borderRadius: 16,
    },
    music: {
      baseVolume: 0.12,
      duckedMultiplier: 0.25,
      fadeSec: 0.3,
    },
  },

  x_bold: {
    id: 'x_bold',
    label: 'X Bold',
    background: {
      type: 'gradient',
      value: 'radial-gradient(1400px circle at 30% 20%, #1a1a2e 0%, #000000 60%, #0a0a0a 100%)',
    },
    text: {
      color: COLORS.text.inverse,
      muted: 'rgba(255,255,255,0.7)',
      accent: '#f7931a',
      fontFamilyHeading: FONTS.heading,
      fontFamilyBody: FONTS.body,
      fontFamilyMono: FONTS.mono,
    },
    card: {
      backgroundColor: 'rgba(0,0,0,0.5)',
      borderRadius: 20,
    },
    music: {
      baseVolume: 0.2,
      duckedMultiplier: 0.2,
      fadeSec: 0.2,
    },
  },
};

export const resolveStylePresetId = (input: unknown): StylePresetId => {
  if (typeof input !== 'string') return 'meta_explainer_dark';

  const raw = input.trim();
  if (raw === '') return 'meta_explainer_dark';

  const lower = raw.toLowerCase();

  // Exact match
  if (lower in STYLE_PRESETS) return lower as StylePresetId;

  // Back-compat / fuzzy mapping for older preset strings.
  if (lower.includes('corporate') || lower.includes('clean') || lower.includes('minimal')) {
    return 'meta_explainer_cream';
  }
  if (lower.includes('neon') || lower.includes('miami') || lower.includes('playful')) {
    return 'meta_bold';
  }
  if (lower.includes('thread') && lower.includes('light')) return 'x_thread_light';
  if (lower.includes('thread') || lower.includes('twitter')) return 'x_thread_dark';
  if (lower.includes('x_bold') || (lower.includes('bold') && lower.includes('x'))) return 'x_bold';

  return 'meta_explainer_dark';
};

export const getStylePreset = (input: unknown): StylePreset => STYLE_PRESETS[resolveStylePresetId(input)];


;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
